import HCaptcha from '@hcaptcha/react-hcaptcha';
import React, { useContext, useEffect, useState } from 'react';
import * as ReCAPTCHA from 'react-google-recaptcha-enterprise';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import Button, { ButtonStyle } from '../common/components/Button/Button';
import ButtonContainer from '../common/components/Button/ButtonContainer';
import Container from '../common/components/Container/Container';
import OwnerSignsMessage from '../common/components/OwnerSignsMessage';
import TitleContainer from '../common/components/TitleContainer/TitleContainer';
import { Action, Provider, Resolver } from '../config';
import { CaptchaConfig, CaptchaProvider } from '../config/networkConfig';
import { FlowConfigContext } from '../providers/FlowConfigContext';
import CaptchaEventPayload from '../services/events/captchaEventPayload';
import { AuthenticationContext } from '../providers/AuthenticationContext';
import withRouteAnalytics from '../services/analytics/withRouteAnalytics';
import { Screens } from '../services/analytics/screens';
import { getGknLocalisedString } from '../providers/GatekeeperNetworkService/utils';
import { GatekeeperNetworkServiceContext } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';

function CaptchaScreen(): JSX.Element {
  const { t } = useTranslation();
  const [selfDeclarationText, setSelfDeclarationText] = useState<string>('');
  const [resultToken, setResultToken] = useState<string>('');
  const { getCurrentToken, isLoggedIn } = useContext(AuthenticationContext);
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);

  const { initialized, setAcceptedDeclaration, flowCancelled, flowSuccessful, action, getProviderConfig } =
    useContext(FlowConfigContext);

  const onRecaptchaChange = (token: string | null) => {
    if (token) {
      setAcceptedDeclaration(selfDeclarationText);
      setResultToken(token);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    if (!resultToken || !initialized) {
      return;
    }
    const runFlowSuccessful = async () => {
      const captchaConfig = getProviderConfig<CaptchaConfig>(Provider.CAPTCHA);
      let authToken;
      // an auth token may not be required for the captcha flow
      if (isLoggedIn()) {
        authToken = await getCurrentToken();
      }
      if (abortController.signal.aborted) {
        return;
      }
      flowSuccessful(new CaptchaEventPayload(captchaConfig.captchaProvider, resultToken, authToken?.success()));
    };
    runFlowSuccessful();
    return () => abortController.abort();
  }, [resultToken, initialized, isLoggedIn]);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    const selfDeclaration = getGknLocalisedString(gknServiceData!, 'acceptedDeclaration');
    if (selfDeclaration) {
      setSelfDeclarationText(t(selfDeclaration));
    }
  }, [initialized]);

  const handleCancel = async () => {
    flowCancelled();
  };

  const renderCaptchaComponent = (): JSX.Element | null => {
    const captchaConfig = getProviderConfig<CaptchaConfig>(Provider.CAPTCHA);
    switch (captchaConfig.captchaProvider) {
      case CaptchaProvider.HCAPTCHA:
        return (
          <HCaptcha
            sitekey={Resolver.getServiceConfig().captchaKeys.hCaptchaKey}
            onVerify={onRecaptchaChange}
            theme="light"
            id="hcatpcha_iframe"
          />
        );
      case CaptchaProvider.RECAPTCHA:
        return (
          <ReCAPTCHA.default
            sitekey={Resolver.getServiceConfig().captchaKeys.reCaptchaKey}
            onChange={onRecaptchaChange}
            type="image"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container iframeContainerCssClass="recaptcha-container">
      <TitleContainer center>
        <h1 className="mb-4" data-testid="recaptchaTitle">
          {t('screens.captchaPass.recaptcha.title', { context: action })}
        </h1>
        <>
          {action === Action.REFRESH && (
            <div
              className="text-lg leading-6 mb-4 text-textParagraph pass-status-text"
              data-testid="recaptchaDescription"
            >
              {t('screens.captchaPass.recaptcha.description', { context: action })}
            </div>
          )}
        </>
      </TitleContainer>
      <>
        {action === Action.ISSUANCE && (
          <div
            data-testid="DECLARATIONS_PANEL"
            className="mb-4 flex-grow-1 flex-shrink-0 declarations text-textParagraph"
          >
            <ReactMarkdown linkTarget="_blank">{selfDeclarationText}</ReactMarkdown>
          </div>
        )}
      </>
      <>{action === Action.ISSUANCE && <OwnerSignsMessage marginBottom={12} />}</>
      <div data-testid="RECAPTCHA_PANEL" className="mb-4">
        {renderCaptchaComponent()}
        <ButtonContainer>
          <Button
            style={ButtonStyle.INVERTED}
            extraClasses="mt-2"
            disabled={false}
            onClick={handleCancel}
            dataTestid="CANCEL_BUTTON"
          >
            <span>{t('screens.captchaPass.terms.cancelLabel')}</span>
          </Button>
        </ButtonContainer>
      </div>
    </Container>
  );
}
export default withRouteAnalytics(CaptchaScreen, Screens.CAPTCHA);
